import React, { useState } from 'react';
import { useSchool as useOps } from '../context/SchoolContext';
import { User, Shield, Search, Plus, Upload, Check, X, UserPlus, ShieldAlert, Mail, Edit } from 'lucide-react';
import ImportModal from '../components/ImportModal';
import LoadingSpinner from '../components/LoadingSpinner';
import './UserManager.css';

const UserManager = () => {
    const {
        users = [], roles = [], pendingUsers = [],
        addUser, updateUser, approveUser, rejectUser, handleTemplateImport,
        loading
    } = useOps();

    const [activeTab, setActiveTab] = useState('authorized');
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        username: '',
        email: '',
        role: 'subscriber'
    });

    const filteredUsers = users.filter(user =>
        user.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_login.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredPending = pendingUsers.filter(user =>
        user.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_login.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleAddUser = async (e) => {
        e.preventDefault();
        await addUser(form);
        setIsAddModalOpen(false);
        setForm({ first_name: '', last_name: '', username: '', email: '', role: 'subscriber' });
    };

    const handleEditClick = (user) => {
        setEditingUser(user);
        // Map user display name back to first/last if possible, or just use what we have
        const names = user.display_name.split(' ');
        setForm({
            first_name: names[0] || '',
            last_name: names.slice(1).join(' ') || '',
            username: user.user_login,
            email: user.email,
            role: user.roles[0] || 'subscriber'
        });
        setIsEditModalOpen(true);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        await updateUser(editingUser.id, form);
        setIsEditModalOpen(false);
        setEditingUser(null);
        setForm({ first_name: '', last_name: '', username: '', email: '', role: 'subscriber' });
    };

    const handleApprove = async (id) => {
        if (window.confirm('Are you sure you want to approve this user?')) {
            await approveUser(id);
        }
    };

    const handleReject = async (id) => {
        if (window.confirm('Are you sure you want to reject this user?')) {
            await rejectUser(id);
        }
    };

    if (loading) return <LoadingSpinner color="indigo-600" message="Loading Access Control..." />;

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Identity & Access Management</h2>
                    <p className="page-subtitle">Manage system users, roles and security approvals</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="btn btn-primary flex items-center gap-2"
                    >
                        <UserPlus size={18} /> Add User
                    </button>
                    <button
                        onClick={() => setIsImportModalOpen(true)}
                        className="btn btn-secondary flex items-center gap-2"
                    >
                        <Upload size={18} /> Bulk Import
                    </button>
                </div>
            </div>

            <div className="tabs-container">
                <button
                    className={`tab-btn ${activeTab === 'authorized' ? 'active' : ''}`}
                    onClick={() => setActiveTab('authorized')}
                >
                    Authorized Personnel
                    <span className="tab-badge">{users.length}</span>
                </button>
                <button
                    className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    Pending Approvals
                    <span className="tab-badge">{pendingUsers.length}</span>
                </button>
            </div>

            <div className="filters-bar">
                <div className="search-input-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name, email or username..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="table-container card fade-in">
                {activeTab === 'authorized' ? (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>User Profile</th>
                                <th>Contact Info</th>
                                <th>System Roles</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length > 0 ? filteredUsers.map(user => (
                                <tr key={user.id}>
                                    <td>
                                        <div className="user-badge">
                                            <div className="user-avatar">{user.display_name[0]}</div>
                                            <div className="user-info">
                                                <div className="name">{user.display_name}</div>
                                                <div className="username">@{user.user_login}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                                            <Mail size={14} className="text-slate-400" />
                                            {user.email}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex flex-wrap gap-1">
                                            {user.roles.map(role => (
                                                <span key={role} className={`role-badge role-${role.toLowerCase()}`}>
                                                    {role}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-badge status-${(user.status || 'approved').toLowerCase()}`}>
                                            {user.status || 'Approved'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="icon-btn"
                                                title="Edit User"
                                                onClick={() => handleEditClick(user)}
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button className="icon-btn" title="Manage Permissions">
                                                <Shield size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="5" className="text-center py-12 text-slate-400 italic">No authorized personnel found.</td></tr>
                            )}
                        </tbody>
                    </table>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Reg. Name</th>
                                <th>Username</th>
                                <th>Email Address</th>
                                <th>Security Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPending.length > 0 ? filteredPending.map(user => (
                                <tr key={user.id}>
                                    <td>
                                        <div className="font-bold text-slate-800 text-sm">
                                            {user.first_name} {user.last_name}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="text-xs font-mono bg-slate-50 px-2 py-1 rounded border border-slate-100 inline-block">
                                            {user.user_login}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="text-sm text-slate-600">{user.email}</div>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-2 text-amber-600 font-bold text-[10px] uppercase">
                                            <ShieldAlert size={14} />
                                            Awaiting Review
                                        </div>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                onClick={() => handleApprove(user.id)}
                                                className="icon-btn approve"
                                                title="Approve Access"
                                            >
                                                <Check size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleReject(user.id)}
                                                className="icon-btn reject"
                                                title="Deny Access"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="5" className="text-center py-12 text-slate-400 italic">No pending applications found.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Add User Modal */}
            {isAddModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content card max-w-md fade-in">
                        <div className="modal-header border-b pb-4 mb-6">
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">Add System User</h3>
                            <button onClick={() => setIsAddModalOpen(false)} className="icon-btn border-none">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleAddUser} className="premium-form">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>First Name</label>
                                    <input
                                        required
                                        type="text"
                                        value={form.first_name}
                                        onChange={e => setForm({ ...form, first_name: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Name</label>
                                    <input
                                        required
                                        type="text"
                                        value={form.last_name}
                                        onChange={e => setForm({ ...form, last_name: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Username</label>
                                <input
                                    required
                                    type="text"
                                    value={form.username}
                                    onChange={e => setForm({ ...form, username: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={e => setForm({ ...form, email: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Assign Role</label>
                                <select
                                    value={form.role}
                                    onChange={e => setForm({ ...form, role: e.target.value })}
                                >
                                    {roles.map(role => (
                                        <option key={role.id} value={role.id}>{role.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button type="button" className="btn btn-secondary flex-1" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary flex-1">Create User</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {isEditModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content card max-w-md fade-in">
                        <div className="modal-header border-b pb-4 mb-6">
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">Edit System User</h3>
                            <button onClick={() => { setIsEditModalOpen(false); setEditingUser(null); }} className="icon-btn border-none">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateUser} className="premium-form">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>First Name</label>
                                    <input
                                        required
                                        type="text"
                                        value={form.first_name}
                                        onChange={e => setForm({ ...form, first_name: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Name</label>
                                    <input
                                        required
                                        type="text"
                                        value={form.last_name}
                                        onChange={e => setForm({ ...form, last_name: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Username (Read Only)</label>
                                <input
                                    readOnly
                                    type="text"
                                    className="bg-slate-50 text-slate-500 cursor-not-allowed"
                                    value={form.username}
                                />
                            </div>
                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={e => setForm({ ...form, email: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Assign Role</label>
                                <select
                                    value={form.role}
                                    onChange={e => setForm({ ...form, role: e.target.value })}
                                >
                                    {roles.map(role => (
                                        <option key={role.id} value={role.id}>{role.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button type="button" className="btn btn-secondary flex-1" onClick={() => { setIsEditModalOpen(false); setEditingUser(null); }}>Cancel</button>
                                <button type="submit" className="btn btn-primary flex-1">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImport={handleTemplateImport}
                title="Import Users"
                templateHeaders={['Username', 'FirstName', 'Surname', 'Email', 'Role']}
                sampleRow={['jsmith', 'John', 'Smith', 'john@sdt.com', 'subscriber']}
            />
        </div>
    );
};

export default UserManager;
